import axios from 'axios';
import {
  AUTH_STORAGE_KEYS,
  buildTenantContext,
  clearAuthSession,
  getDefaultApiRoot,
  normalizeBaseUrl,
  saveAuthSession,
} from './tenantSession';
import { confirmarGlobal } from './confirmBridge';
import { DEVICE_ID_HEADER, getDeviceId } from './deviceId';
import { PIN_HEADER, getPinGuardado } from './portalPin';
import { esFalloDeRed } from '../utils/fallosDeRed';
import { invalidarCatalogo } from './catalogoCache';

const DEFAULT_API_ROOT = getDefaultApiRoot();

// URL base del backend y URL raíz de la API
const API_ROOT = normalizeBaseUrl(process.env.REACT_APP_API_ROOT, DEFAULT_API_ROOT);
const API_URL = normalizeBaseUrl(process.env.REACT_APP_API_URL, `${API_ROOT}/taller`) + '/';

// Crear instancia de axios con configuración base
const apiClient = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Adjunta a `instance` el mismo pipeline de auth que usa apiClient: token en
 * cada request, refresh single-flight ante 401 y redirect a /login si el
 * refresh también falla. Cualquier cliente axios propio (p. ej. el de la
 * consola de plataforma) tiene que pasar por acá — si arma su propio
 * interceptor de request "a mano" y se queda ahí, un access token vencido
 * lo deja sin forma de recuperarse: cada request falla con 401 para siempre
 * y no hay manera de volver a /login salvo recargar y perder el estado.
 */
function attachAuthInterceptors(instance) {
  instance.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem(AUTH_STORAGE_KEYS.accessToken);
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        try {
          const access = await refrescarTokenUnaVez();
          originalRequest.headers.Authorization = `Bearer ${access}`;
          return instance(originalRequest);
        } catch (refreshError) {
          // Sin conexión la sesión se conserva: el token no fue rechazado, no
          // se pudo preguntar. Ver `esFalloDeRed`.
          if (esFalloDeRed(refreshError)) {
            return Promise.reject(error);
          }
          clearAuthSession();
          window.location.href = '/login';
          return Promise.reject(error);
        }
      }

      return Promise.reject(error);
    }
  );
}

// Interceptor para agregar el token de autenticación y, si este dispositivo ya
// lo guardó, el PIN del portal QR/app móvil (modo restringido — ver
// services/portalPin.js). El backend lo ignora salvo que el tenant lo exija.
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(AUTH_STORAGE_KEYS.accessToken);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    const pin = getPinGuardado();
    if (pin) {
      config.headers[PIN_HEADER] = pin;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Single-flight de refresh: con ROTATE_REFRESH_TOKENS + BLACKLIST_AFTER_ROTATION en el backend,
// el refresh token queda invalidado apenas se usa una vez. Si varias peticiones en paralelo
// (Promise.all) reciben 401 al mismo tiempo, cada una intentaba refrescar por su cuenta con el
// mismo refresh token: la primera lo rotaba y blacklisteaba, y el resto fallaba con 401,
// disparando clearAuthSession()/redirect a /login o dejando timeouts colgados aguas arriba.
// Compartir una única promesa de refresh evita la carrera.
let refreshPromise = null;

async function refrescarTokenUnaVez() {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    const refreshToken = localStorage.getItem(AUTH_STORAGE_KEYS.refreshToken);
    if (!refreshToken) {
      throw new Error('No hay refresh token disponible');
    }
    // El header de dispositivo va también acá: este es el refresh que más se
    // usa en la app, y es el que mantiene viva la marca de actividad con la
    // que se mide la duración real de cada sesión.
    const response = await axios.post(
      `${API_ROOT}/auth/token/refresh/`,
      { refresh: refreshToken },
      { headers: { [DEVICE_ID_HEADER]: getDeviceId() } },
    );
    const { access, refresh } = response.data;
    saveAuthSession({
      access,
      refresh,
      user: JSON.parse(localStorage.getItem(AUTH_STORAGE_KEYS.userData) || 'null'),
      tenant: localStorage.getItem(AUTH_STORAGE_KEYS.tenantSlug),
      tenantContext: buildTenantContext(process.env.REACT_APP_API_ROOT),
    });
    return access;
  })().finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
}

// Interceptor para manejar respuestas de error
apiClient.interceptors.response.use(
  (response) => {
    // Toda escritura invalida el catálogo del recurso tocado. Sin esto, un
    // alta o edición hecha desde un modal de configuración no se vería en las
    // pantallas que ya tienen ese catálogo cacheado, y el caché pasaría de ser
    // una optimización a una fuente de datos viejos.
    const metodo = String(response?.config?.method || '').toLowerCase();

    // Red de seguridad contra el truncamiento silencioso.
    //
    // Una respuesta paginada con `next` significa que quedaron filas afuera. Si
    // además quien la pidió NO especificó `page_size`, es que se apoyó en el
    // default del backend — y una pantalla que se apoya en el default y no
    // recorre `next` muestra datos incompletos sin dar ningún error: el usuario
    // ve una lista más corta y no tiene forma de saberlo. Es el modo de falla
    // que hay que poder detectar antes de que llegue a un cliente.
    if (metodo === 'get' && response?.data?.next && !('page_size' in (response.config?.params || {}))) {
      const url = String(response.config?.url || '');
      if (!/\?.*page_size=/.test(url)) {
        console.warn(
          `[paginación] ${url} devolvió una página incompleta (hay más en "next") y no se pidió page_size. ` +
          'Si esta pantalla necesita el listado completo, tiene que pedir page_size explícito o recorrer las páginas; ' +
          'si es un buscador, debería buscar server-side (ver useBusquedaRemota).'
        );
      }
    }

    if (metodo && metodo !== 'get' && metodo !== 'head' && metodo !== 'options') {
      const recurso = String(response?.config?.url || '')
        .replace(/^\/+/, '')
        .split('?')[0]
        .split('/')
        .filter(Boolean)[0];
      if (recurso) invalidarCatalogo(recurso);
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Confirmación global: el backend devuelve 409 'requiere_confirmacion' cuando una
    // acción tiene una consecuencia que el usuario debe aceptar antes de seguir
    // (repuestos entregados sin devolver, un cambio que desactualiza el plan de
    // mantenimiento del equipo, etc.). Mostramos el detalle y reintentamos forzando,
    // sin que cada pantalla lo maneje.
    //
    // El título/etiqueta salen del propio payload: así un caso nuevo solo necesita
    // levantar la excepción en el backend. Los valores por defecto son los del
    // primer caso que existió (repuestos), para no cambiar lo que ya se ve.
    const conf = error.response?.data;
    if (
      error.response?.status === 409 &&
      conf?.requiere_confirmacion &&
      !originalRequest._confirmRetry
    ) {
      const ok = await confirmarGlobal({
        titulo: conf.titulo || 'Repuestos sin devolver',
        mensaje: conf.detail || 'Hay repuestos entregados por almacén sin usar ni devolver. ¿Continuar de todos modos?',
        labelConfirmar: conf.label_confirmar || 'Sí, continuar',
        variante: conf.variante || 'danger',
      });
      if (!ok) {
        return Promise.reject(error);
      }
      originalRequest._confirmRetry = true;
      try {
        const isForm = typeof FormData !== 'undefined' && originalRequest.data instanceof FormData;
        if (isForm) {
          originalRequest.data.set('forzar', 'true');
        } else {
          // En el interceptor de respuesta, config.data ya viene serializado a string.
          // Lo dejamos como objeto para que axios lo serialice UNA sola vez en el reintento
          // (si lo dejáramos como string, el transformRequest lo volvería a stringificar).
          let body = {};
          if (typeof originalRequest.data === 'string' && originalRequest.data) {
            body = JSON.parse(originalRequest.data);
          } else if (originalRequest.data && typeof originalRequest.data === 'object') {
            body = originalRequest.data;
          }
          body.forzar = true;
          originalRequest.data = body;
        }
      } catch (_e) {
        // Si no se pudo inyectar el flag, dejar el reject original.
        return Promise.reject(error);
      }
      return apiClient(originalRequest);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const access = await refrescarTokenUnaVez();
        originalRequest.headers.Authorization = `Bearer ${access}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        // Sin conexión no se toca la sesión: el servidor no rechazó el token,
        // no se pudo llegar a preguntarle. Ver `esFalloDeRed`. Con respuesta
        // del servidor sí — ahí el refresh está vencido o revocado de verdad.
        if (esFalloDeRed(refreshError)) {
          return Promise.reject(error);
        }
        clearAuthSession();
        window.location.href = '/login';
        return Promise.reject(error);
      }
    }

    // 429: el backend aplica límite de peticiones por usuario. Sin un mensaje propio la UI
    // mostraba el error crudo de axios ("Request failed with status code 429"), que no le
    // dice nada al usuario. Se normaliza a un texto accionable, usando el tiempo de espera
    // que informa el backend en Retry-After cuando está disponible.
    if (error.response?.status === 429) {
      const retryAfter = Number(error.response.headers?.['retry-after']);
      const espera = Number.isFinite(retryAfter) && retryAfter > 0
        ? (retryAfter >= 60
          ? ` Probá de nuevo en ${Math.ceil(retryAfter / 60)} minuto(s).`
          : ` Probá de nuevo en ${retryAfter} segundo(s).`)
        : ' Esperá unos minutos y volvé a intentar.';
      const data = error.response.data;
      const detalleServidor = (data && typeof data === 'object' && typeof data.detail === 'string')
        ? data.detail
        : null;
      error.response.data = {
        detail: detalleServidor && !/throttl|request/i.test(detalleServidor)
          ? detalleServidor
          : `Se alcanzó el límite de consultas al servidor.${espera}`,
      };
    }

    // Normalizar errores 5xx: el servidor puede devolver HTML (página de error de Django)
    // en lugar de JSON. Solo reemplazamos si el cuerpo es HTML o está vacío —
    // si ya es un objeto con campos de validación (p.ej. {cantidad: [...]}) lo dejamos pasar
    // para que el componente muestre el mensaje específico al usuario.
    if (error.response?.status >= 500) {
      const data = error.response.data;
      const isHtml = typeof data === 'string' && data.trimStart().startsWith('<');
      const hasUsefulContent = data && typeof data === 'object' && Object.keys(data).length > 0;
      if (isHtml || (!hasUsefulContent)) {
        error.response.data = { detail: 'Ocurrió un error en el servidor. Intentá nuevamente en unos momentos.' };
      }
    }

    return Promise.reject(error);
  }
);

export { apiClient, API_URL, API_ROOT, buildTenantContext, attachAuthInterceptors };
export default apiClient;
